import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { installationItemService, installationTypeService } from '../services/api';

export default function Items() {
  const [items, setItems] = useState([]);
  const [installationTypes, setInstallationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    installation_type: 'all',
    unit: 'piece',
    weight: 0,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const [itemsResponse, typesResponse] = await Promise.all([
        installationItemService.getItems(),
        installationTypeService.getTypes(),
      ]);
      setItems(itemsResponse.data || []);
      if (typesResponse.success && typesResponse.data?.length) {
        setInstallationTypes(typesResponse.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditMode(true);
      setSelectedItem(item);
      setFormData({
        name: item.name,
        description: item.description || '',
        installation_type: item.installation_type,
        unit: item.unit,
        weight: item.weight || 0,
      });
    } else {
      setEditMode(false);
      setSelectedItem(null);
      setFormData({
        name: '',
        description: '',
        installation_type: 'all',
        unit: 'piece',
        weight: 0,
      });
    }
    setDialogOpen(true);
    setError('');
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Item name is required');
      return;
    }

    try {
      if (editMode) {
        await installationItemService.updateItem(selectedItem._id, formData);
        setSuccess('Item updated successfully');
      } else {
        await installationItemService.createItem(formData);
        setSuccess('Item created successfully');
      }
      setDialogOpen(false);
      loadItems();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    try {
      await installationItemService.deleteItem(itemToDelete._id);
      setSuccess(`Item "${itemToDelete.name}" deleted successfully`);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      loadItems();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const getInstallationTypeLabel = (type) => {
    if (type === 'all') return 'All Types';
    const t = installationTypes.find((x) => x.code === type);
    if (t) return t.name_en ? `${t.name_ar} (${t.name_en})` : t.name_ar;
    const fallback = { buildings: 'عمارات (Buildings)', pole: 'بالو (Pole)', wall: 'جداري (Wall)', standalone: 'منسلة (Standalone)' };
    return fallback[type] || type;
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Item Name',
      width: 250,
      renderCell: (params) => <strong>{params.value}</strong>,
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 300,
    },
    {
      field: 'installation_type',
      headerName: 'Installation Type',
      width: 200,
      renderCell: (params) => (
        <Chip
          label={getInstallationTypeLabel(params.value)}
          color={params.value === 'all' ? 'secondary' : 'primary'}
          size='small'
        />
      ),
    },
    {
      field: 'unit',
      headerName: 'Unit',
      width: 100,
    },
    {
      field: 'weight',
      headerName: 'Weight',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value || 0}
          color='info'
          size='small'
        />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size='small'
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      renderCell: (params) => (
        <Box>
          <Button
            variant='outlined'
            size='small'
            onClick={() => handleOpenDialog(params.row)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            variant='outlined'
            size='small'
            color='error'
            startIcon={<DeleteIcon />}
            onClick={() => handleDelete(params.row)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
        <Typography variant='h4'>Installation Items Management</Typography>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Item
        </Button>
      </Box>

      {error && !dialogOpen && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <DataGrid
            rows={items}
            columns={columns}
            getRowId={(row) => row._id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            autoHeight
            disableRowSelectionOnClick
          />
        </CardContent>
      </Card>

      {/* Create/Edit Item Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>{editMode ? 'Edit Item' : 'Create New Item'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            autoFocus
            margin='dense'
            label='Item Name'
            type='text'
            fullWidth
            variant='outlined'
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
            placeholder='e.g., 1m Cable, Connector, Switch'
          />

          <TextField
            margin='dense'
            label='Description'
            type='text'
            fullWidth
            variant='outlined'
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Installation Type</InputLabel>
            <Select
              value={formData.installation_type}
              label='Installation Type'
              onChange={(e) => setFormData({ ...formData, installation_type: e.target.value })}
            >
              <MenuItem value='all'>All Types</MenuItem>
              {installationTypes.map((t) => (
                <MenuItem key={t._id} value={t.code}>
                  {t.name_en ? `${t.name_ar} (${t.name_en})` : t.name_ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin='dense'
            label='Unit'
            type='text'
            fullWidth
            variant='outlined'
            value={formData.unit}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            placeholder='e.g., meter, piece, kg'
            sx={{ mb: 2 }}
          />

          <TextField
            margin='dense'
            label='Weight'
            type='number'
            fullWidth
            variant='outlined'
            value={formData.weight}
            onChange={(e) => setFormData({ ...formData, weight: parseInt(e.target.value) || 0 })}
            placeholder='Higher weight appears first (default: 0)'
            inputProps={{ min: 0 }}
            helperText='Items with higher weight appear first in the list'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant='contained'>
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Item Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Item</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography>
            Are you sure you want to delete the item <strong>"{itemToDelete?.name}"</strong>?
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            This will soft delete the item. The item will be marked as inactive and will not appear in the list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setItemToDelete(null);
            setError('');
          }}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant='contained' color='error'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

